import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, ShieldCheck, ShieldAlert, FileText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EwayBanner } from "@/components/EwayBanner";

export function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const dashPath =
    user?.role === "sender" ? "/sender" :
    user?.role === "lorry_owner" ? "/owner" :
    user?.role === "driver" ? "/driver" :
    user?.role === "admin" ? "/admin" :
    user?.role === "super_admin" ? "/superadmin" :
    user?.role === "accounts" ? "/accounts" :
    user?.role === "loading_supervisor" ? "/ops" : "/";

  const isProvider = user?.role === "lorry_owner" || user?.role === "driver";
  const isOps = ["loading_supervisor", "accounts", "super_admin", "admin"].includes(user?.role);

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#2A2A2A]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {isOps ? (
            <>
              <Link to="/ops" className="overline text-zinc-400 hover:text-white">Ops</Link>
              <Link to="/accounts" className="overline text-zinc-400 hover:text-white">Accounts</Link>
              <Link to="/customers" data-testid="nav-customers" className="overline text-zinc-400 hover:text-white">Customers</Link>
              <Link to="/parties" data-testid="nav-parties" className="overline text-zinc-400 hover:text-white">Parties</Link>
              <Link to="/fleet" className="overline text-zinc-400 hover:text-white">Fleet</Link>
              <Link to="/drivers-db" className="overline text-zinc-400 hover:text-white">Drivers</Link>
              <Link to="/track" className="overline text-zinc-400 hover:text-white">Track</Link>
              {["super_admin", "admin"].includes(user?.role) && (
                <Link to="/recycle-bin" data-testid="nav-recycle" className="overline text-zinc-400 hover:text-white">Recycle</Link>
              )}
            </>
          ) : (
            <>
              <a href="/#how" className="overline text-zinc-400 hover:text-white transition-colors">How it works</a>
              <a href="/#senders" className="overline text-zinc-400 hover:text-white transition-colors">Post Load</a>
              <a href="/#drivers" className="overline text-zinc-400 hover:text-white transition-colors">Hire Driver</a>
              <Link to="/track" className="overline text-zinc-400 hover:text-white">Track Shipment</Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user && user.id ? (
            <>
              {isProvider && (
                <>
                  <Link to="/documents" data-testid="nav-documents-link"
                    className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-zinc-300 hover:text-white">
                    <FileText className="w-4 h-4" /> Documents
                  </Link>
                  {user.verified
                    ? <span data-testid="verified-badge" title="Verified by admin" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-700 bg-emerald-900/20 text-emerald-300 rounded-sm"><ShieldCheck className="w-3 h-3" /> Verified</span>
                    : <span data-testid="unverified-badge" title="Awaiting document verification" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] rounded-sm"><ShieldAlert className="w-3 h-3" /> Pending</span>}
                </>
              )}
              <Link to={dashPath} data-testid="nav-dashboard-link"
                className="text-sm font-medium text-zinc-300 hover:text-white">Dashboard</Link>
              <Button data-testid="nav-logout-btn" onClick={onLogout} variant="outline" size="sm"
                className="rounded-sm border-[#2A2A2A] bg-transparent text-white hover:bg-[#141414]">
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium text-zinc-300 hover:text-white">Login</Link>
              <Link to="/register">
                <Button data-testid="nav-register-btn" size="sm"
                  className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
      <EwayBanner />
    </header>
  );
}
